// apps/console/src/services/api/sos-alert-queue.ts
//
// The queue behind the emergency card (#1272): which emergencies are RINGING for this operator.
//
// React-free and module-level, mirroring insufficient-credits-emitter.ts: a Set of listeners plus a
// module-level value. It sits between the alert store's ARRIVAL event and the card a dashboard page
// mounts, and answers the one question the store cannot — which emergency is this operator being
// asked about right now.
//
// It is a QUEUE and not a slot because two critical alarms landing in the same second is the
// ordinary shape of a real incident, not an edge case: a fall trips, the worker presses SOS, the
// helmet reports no movement. A slot would overwrite the first and hide it forever.
//
// But the queue holds AT MOST ONE ENTRY PER DEVICE, and that entry is the newest critical alarm
// that device has raised. One worker pressing SOS five times is one emergency, not five. Collapsing
// PER DEVICE rather than fleet-wide is the whole point: "stop asking me twice about one worker" is
// safe, "stop telling me about the second worker" is not.
//
// RINGING ONLY (2026-10). An entry rings for `SOS_RING_WINDOW_MS`, then it is MISSED and leaves the
// queue — like a call nobody picked up. It is not answered and not removed from anywhere else: it is
// still open in the store, the bell and the log, and the missed list (`sos-missed.ts`, derived from
// the store) shows it until someone calls back or dismisses it. An alarm that is ALREADY missed when
// it reaches the queue (the backlog of an old SOS) never rings at all; it goes straight to that list.
//
// The card is mounted on a DASHBOARD only, and only for a device that dashboard shows. So the queue
// is fed through a predicate the mounting dashboard supplies.

import {
  readRobotAlerts,
  subscribeRobotAlertArrivals,
  subscribeRobotAlerts,
  type RobotAlertItem,
} from './robot-alerts-store'
import { sosIsMissed, sosMissedDeadline, sosRecencyOf } from './sos-alert-timeout'
import {
  clearAnsweredAlerts,
  forgetAnsweredAlert,
  readAnsweredAlerts,
  rememberAnsweredAlert,
} from './sos-answered-store'

/** The emergency another surface just took, so the card can say so instead of vanishing. */
export type SosHandledElsewhere = RobotAlertItem

/** What the card renders. */
export interface SosQueueState {
  /** Every emergency RINGING for this operator, oldest first. The whole list is on screen. */
  queue: RobotAlertItem[]
  /** The head — the default selection, and the one answered first if the operator just presses. */
  current: RobotAlertItem | null
  /** How many more emergencies are queued behind `current`. */
  waiting: number
  /**
   * Alerts that stopped ringing HERE before the shared clocks say so (this machine's clock runs
   * behind the broker's). The missed list includes them, so leaving the card never means leaving
   * the screen.
   */
  rangOutIds: ReadonlySet<string>
  /** An emergency answered on another console or surface, shown briefly; null otherwise. */
  handledElsewhere: SosHandledElsewhere | null
}

type Listener = (state: SosQueueState) => void

/** Whether the mounted dashboard shows this device — the only alerts it may interrupt with. */
export type SosCovers = (robotId: string) => boolean

/** How long the "answered elsewhere" note stays up. */
export const SOS_HANDLED_NOTE_MS = 4_000

/**
 * How often a connected queue re-offers the store's alerts and re-checks the ring window.
 *
 * The card leaves at the exact deadline through its own timer; this tick is what puts an alarm
 * that RESUMED back in front of the operator (the store announces an arrival only once).
 */
export const SOS_SWEEP_MS = 15_000

const listeners = new Set<Listener>()

/** `offeredAt` is when this emergency was put to an operator, stamped on THIS machine. */
interface QueueEntry {
  item: RobotAlertItem
  offeredAt: number
}

/** Oldest first; the head is what the operator is looking at. */
let entries: QueueEntry[] = []

/**
 * Alert ids this session will not put to the operator again: ANSWERED (Accept or Decline, persisted
 * in `sos-answered-store.ts` so a reload does not re-ask) and SUPERSEDED (a newer alarm for the same
 * device replaced it — session bookkeeping only, never persisted).
 */
const answered = new Set<string>(readAnsweredAlerts())

/** Rang out on THIS screen, with the alarm's recency at that moment — a newer sighting rings again. */
const rangOut = new Map<string, number>()

/** Answers this tab is in the middle of giving, so their store removal is not "answered elsewhere". */
const answering = new Set<string>()

/** Every alert id the store has delivered since the last connect — "gone" needs "was there". */
const seenInStore = new Set<string>()

let handledElsewhere: SosHandledElsewhere | null = null
let handledTimer: ReturnType<typeof setTimeout> | null = null
let deadlineTimer: ReturnType<typeof setTimeout> | null = null
let connections = 0

/**
 * Which alerts ring. `critical` and nothing else: the helmet translator assigns it to the alarms that
 * mean *go now* — fall, restricted-area entry, no movement, SOS, SOS button, escalated SOS,
 * live-electricity proximity, abnormal heart rate — and widening it would dilute the tier.
 */
function isCritical(item: RobotAlertItem): boolean {
  return item.severity === 'critical'
}

function state(): SosQueueState {
  const queue = entries.map(e => e.item)

  return {
    queue,
    current: queue[0] ?? null,
    waiting: Math.max(0, queue.length - 1),
    rangOutIds: new Set(rangOut.keys()),
    handledElsewhere,
  }
}

function notify(): void {
  const snapshot = state()

  listeners.forEach(l => l(snapshot))
}

/**
 * Whether `incoming` should replace `held` as this device's one queued emergency — decided on the
 * TIMESTAMP, never on arrival order, so a remount shows the same emergency as before it.
 */
function supersedes(incoming: RobotAlertItem, held: RobotAlertItem): boolean {
  const a = sosRecencyOf(incoming)
  const b = sosRecencyOf(held)

  if (Number.isFinite(a) && Number.isFinite(b)) return a > b

  return Number.isFinite(a)
}

/** Whether an alert that rang out here has been heard from SINCE — then it rings again. */
function reportedAgainSince(item: RobotAlertItem): boolean {
  const before = rangOut.get(item.alertId)
  const now = sosRecencyOf(item)

  return before !== undefined && Number.isFinite(now) && Number.isFinite(before) && now > before
}

/** Arm one timer for the earliest ringing deadline, so a card leaves at 2:00 and not at the tick. */
function scheduleDeadline(): void {
  if (deadlineTimer) clearTimeout(deadlineTimer)
  deadlineTimer = null

  if (connections === 0 || entries.length === 0) return

  const earliest = Math.min(...entries.map(e => sosMissedDeadline(e.item, e.offeredAt)))

  deadlineTimer = setTimeout(() => sweepMissedSosAlerts(), Math.max(0, earliest - Date.now()) + 5)
}

/** Replace the queue, then tell listeners and re-arm the deadline. */
function commit(next: QueueEntry[]): void {
  entries = next
  notify()
  scheduleDeadline()
}

/**
 * Offer an alert to the queue. Non-critical alerts, duplicates and alerts that already rang out are
 * ignored — a missed alarm belongs in the missed list, never back on the card. A newer alarm for a
 * device already queued REPLACES that entry in place (fleet order stays first-distress-first); the
 * loser is marked answered for this session, and stays open everywhere else.
 */
export function enqueueSosAlert(item: RobotAlertItem, now: number = Date.now()): void {
  if (!isCritical(item) || sosIsMissed(item, now, now)) return

  if (rangOut.has(item.alertId)) {
    if (!reportedAgainSince(item)) return
    rangOut.delete(item.alertId)
  }

  const same = entries.findIndex(e => e.item.alertId === item.alertId)

  // Already ringing: a fresh sighting restarts its clock, which is how an alarm still being reported
  // keeps ringing.
  if (same !== -1) {
    const held = entries[same]

    if (held && supersedes(item, held.item)) commit(entries.map((e, i) => (i === same ? { item, offeredAt: now } : e)))

    return
  }

  const at = entries.findIndex(e => e.item.robotId === item.robotId)

  if (at === -1) {
    commit([...entries, { item, offeredAt: now }])

    return
  }

  const held = entries[at]

  if (!held || !supersedes(item, held.item)) {
    if (held) answered.add(item.alertId)

    return
  }

  answered.add(held.item.alertId)
  commit(entries.map((e, i) => (i === at ? { item, offeredAt: now } : e)))
}

/** An answer is about to be given from this tab — its store removal is not someone else's. */
export function beginSosAnswer(alertId: string): void {
  answering.add(alertId)
}

/**
 * Record that the operator answered `alertId` (Accept or Decline) and drop it from the queue. Answers
 * ANY queued emergency, not just the head — the operator picks which one. Removal is by identity, so
 * naming one already gone is a no-op rather than a mis-consumption.
 *
 * Returns an UNDO, because the card advances on the click while the answer is still on its way to
 * the server. If the server refuses it, nothing was recorded, and the emergency must ring again
 * where it was rather than sit answered on this screen alone. The undo reports whether the emergency
 * went back on the card.
 */
export function answerSosAlert(alertId: string): () => boolean {
  answering.delete(alertId)

  const at = entries.findIndex(e => e.item.alertId === alertId)
  const entry = entries[at]

  if (!entry) return () => false

  answered.add(alertId)
  rememberAnsweredAlert(alertId)
  commit(entries.filter((_, i) => i !== at))

  return () => reinstate(entry, at)
}

/**
 * Put a refused answer's emergency back where it stood, with the clock it was ringing on, so the
 * ring window is not reset by a failed request. Not when another entry now stands for that device: a
 * newer alarm from the same worker is ringing, and the queue holds one emergency per device.
 */
function reinstate(entry: QueueEntry, at: number): boolean {
  const id = entry.item.alertId

  answered.delete(id)
  forgetAnsweredAlert(id)

  if (entries.some(e => e.item.robotId === entry.item.robotId)) return false

  commit([...entries.slice(0, at), entry, ...entries.slice(at)])

  return true
}

/**
 * Move every entry whose ring window has passed out of the queue. NOT marked answered — nobody
 * answered it — and remembered in `rangOut`, so the missed list keeps it and a re-offer of the same
 * sighting does not ring it again. Exported so a spec can drive it with an explicit clock.
 */
export function sweepMissedSosAlerts(now: number = Date.now()): void {
  ringOut(entries.filter(e => sosIsMissed(e.item, e.offeredAt, now)))
}

/** Take these entries off the card as missed (not answered), remembering where each rang out. */
function ringOut(rang: QueueEntry[]): void {
  if (rang.length === 0) {
    scheduleDeadline()

    return
  }

  for (const e of rang) rangOut.set(e.item.alertId, sosRecencyOf(e.item))
  commit(entries.filter(e => !rang.includes(e)))
}

/** Clear the note after its time, unless a newer one replaced it. */
function showHandledElsewhere(note: SosHandledElsewhere): void {
  if (handledTimer) clearTimeout(handledTimer)
  handledElsewhere = note
  handledTimer = setTimeout(() => {
    handledElsewhere = null
    handledTimer = null
    notify()
  }, SOS_HANDLED_NOTE_MS)
}

/**
 * The store changed. A ringing emergency the store no longer holds was acknowledged or resolved
 * somewhere else — another operator, or this one on the map — so it stops ringing here, like a call
 * picked up on another phone, and the card says so for a moment instead of just vanishing.
 */
function onStoreChange(items: RobotAlertItem[]): void {
  const held = new Map(items.map(a => [a.alertId, a]))

  for (const id of held.keys()) seenInStore.add(id)

  // The broker stamped it missed (it reached us on the poll): off the card now, not at our own
  // deadline — every console must agree on when the ringing stopped.
  ringOut(entries.filter(e => held.get(e.item.alertId)?.missedAt))

  const gone = entries.filter(
    e => !held.has(e.item.alertId) && seenInStore.has(e.item.alertId) && !answering.has(e.item.alertId),
  )

  if (gone.length === 0) return

  showHandledElsewhere(gone[0]!.item)
  commit(entries.filter(e => !gone.includes(e)))
}

/** Subscribe; immediately receives the current state. Returns an unsubscribe fn. */
export function subscribeSosAlerts(listener: Listener): () => void {
  listeners.add(listener)
  listener(state())

  return () => {
    listeners.delete(listener)
  }
}

/**
 * Feed this queue from the alert store, admitting only the devices `covers` says this dashboard
 * shows. Returns a release fn.
 *
 * In this order, because the card subscribes right after and must see all of it:
 *  1. Drop queued emergencies the new dashboard does not show (still open in the store).
 *  2. Offer the BACKLOG — every open alert held right now. An SOS that arrived while the operator was
 *     on another page was announced then and never will be again; this is how it still reaches them.
 *  3. Subscribe to arrivals, to the store itself (answered elsewhere), and to the sweep tick.
 */
export function connectSosAlertQueue(covers: SosCovers): () => void {
  const held = readRobotAlerts()
  const heldIds = new Set(held.map(a => a.alertId))
  // Never prune a PERSISTED answer, only a session one: on a reload the card connects BEFORE the
  // snapshot lands, so `held` is empty, and pruning a persisted answer against it would forget the
  // decline a fraction of a second before the declined alarm arrived.
  const persisted = new Set(readAnsweredAlerts())

  for (const id of answered) if (!heldIds.has(id) && !persisted.has(id)) answered.delete(id)
  for (const id of rangOut.keys()) if (!heldIds.has(id)) rangOut.delete(id)
  seenInStore.clear()

  connections += 1

  const before = entries.length

  entries = entries.filter(e => covers(e.item.robotId))
  if (entries.length !== before) notify()

  // The `answered` check lives on the shared feed, not only on the backlog loop: on a reload the
  // alarm reaches this queue as an ARRIVAL (the snapshot lands after the card mounts).
  const offer = (item: RobotAlertItem) => {
    if (answered.has(item.alertId)) return
    if (covers(item.robotId)) enqueueSosAlert(item)
  }

  // Both directions: move what rang out, and re-admit an alarm that RESUMED (a fresh `lastSeenAt`
  // produces no new arrival). Idempotent — `enqueueSosAlert` de-duplicates and collapses per device.
  const reconcile = () => {
    sweepMissedSosAlerts()
    for (const item of readRobotAlerts()) offer(item)
  }

  for (const item of held) offer(item)
  sweepMissedSosAlerts()

  const stopArrivals = subscribeRobotAlertArrivals(offer)
  const stopStore = subscribeRobotAlerts(onStoreChange)
  // The timer lives with the CONNECTION, not the module: a queue nothing is connected to has no
  // operator to interrupt.
  const tick = setInterval(reconcile, SOS_SWEEP_MS)

  return () => {
    clearInterval(tick)
    stopArrivals()
    stopStore()
    connections = Math.max(0, connections - 1)
    scheduleDeadline()
  }
}

/** Re-read the persisted answers, as a page load does — lets a spec simulate a RELOAD honestly. */
export function hydrateAnsweredFromStorage(): void {
  for (const id of readAnsweredAlerts()) answered.add(id)
}

/** Test-only reset of the module state. Clears the persisted answers too — a clean slate is clean. */
export function __resetSosAlertQueueForTests(): void {
  listeners.clear()
  answered.clear()
  clearAnsweredAlerts()
  rangOut.clear()
  answering.clear()
  seenInStore.clear()
  if (handledTimer) clearTimeout(handledTimer)
  if (deadlineTimer) clearTimeout(deadlineTimer)
  handledTimer = null
  deadlineTimer = null
  handledElsewhere = null
  connections = 0
  entries = []
}
